/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { ActionButton, View, Text } from '@components/atoms'
import { Modal } from '@components/molecules'
import { GridToolBar } from './GridToolBar'
import { CircularProgress } from '@mui/material'
import {
	DataGrid,
	DataGridProps,
	GridValidRowModel,
	GridColDef,
	GridCellEditStopParams,
	GridCellEditStopReasons,
	MuiEvent,
	GridEventListener,
	GridRowEditStopReasons,
	GridRowModesModel,
	GridRowsProp,
	GridRowModes,
	GridRowModel,
	GridRenderCellParams,
	useGridApiRef,
	GridPagination,
	useGridApiContext,
	useGridSelector,
	gridPageCountSelector,
	GridRenderEditCellParams,
	GridEditInputCell,
	GridEditBooleanCell,
	GridRowParams,
	GridPreProcessEditCellProps,
	GridRowId,
	GridEditCellValueParams,
	GridRowSelectionModel,
} from '@mui/x-data-grid'
import MuiPagination from '@mui/material/Pagination'
import { TablePaginationProps } from '@mui/material/TablePagination'
import { GridColumnType } from '@grid'
import { randomId } from '@mui/x-data-grid-generator'
import { styled } from '@mui/material/styles'
import Tooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip'

import { grey, pink } from '@mui/material/colors'

interface PropsType extends Omit<DataGridProps, 'columns'> {
	rows: GridRowsProp
	columns: GridColumnType
	loading?: boolean
	onAction?: (iudFlag: string, aRow: GridRowModel) => void
	onAdd?: (aRow: GridRowModel) => void
	onUpdate?: (aRow: GridRowModel) => void
	onDelete?: (aRow: GridRowModel) => void
	renderCell?: {
		[key: string]: (params: GridRenderCellParams) => JSX.Element
	}
	renderEditCell?: {
		[key: string]: (params: GridRenderCellParams) => JSX.Element
	}
	isEditable?: boolean
	checkboxSelection?: boolean
	rowSelectionModel?: GridRowSelectionModel
	onRowSelectionModelChange?: (rowSelectionModel: GridRowSelectionModel) => void
}

function Pagination({
	page,
	onPageChange,
	className,
}: Pick<TablePaginationProps, 'page' | 'onPageChange' | 'className'>) {
	const apiRef = useGridApiContext()
	const pageCount = useGridSelector(apiRef, gridPageCountSelector)

	return (
		<MuiPagination
			color="primary"
			className={className}
			count={pageCount}
			page={page + 1}
			onChange={(event, newPage) => {
				onPageChange(event as any, newPage - 1)
			}}
		/>
	)
}

function CustomPagination(props: any) {
	return <GridPagination ActionsComponent={Pagination} {...props} />
}

declare module '@mui/x-data-grid' {
	interface ToolbarPropsOverrides {
		setRows: (newRows: (oldRows: GridRowsProp) => GridRowsProp) => void
		setRowModesModel: (
			newModel: (oldModel: GridRowModesModel) => GridRowModesModel,
		) => void
		isLoading: boolean
		apiRef: React.MutableRefObject<any>
		onAddClick: () => void
	}
}

export const Grid = (props: PropsType) => {
	// state
	const [rows, setRows] = React.useState(props.rows)
	const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({})
	const [showModal, setShowModal] = React.useState<boolean>(false)
	const [deleteRow, setDeleteRow] = React.useState<GridRowModel>({})
	const [gridEditCellValueParams, setGridEditCellValueParams] = React.useState<
		GridEditCellValueParams & { errorMessage: string }
	>({
		id: '',
		errorMessage: '',
		field: '',
		value: '',
	})

	// variables
	const isLoading: boolean = props.loading ?? false
	const isEditable: boolean = props.isEditable ?? true
	const checkboxSelection: boolean = props.checkboxSelection ?? false

	const apiRef = useGridApiRef()

	React.useEffect(() => {
		setRows(props.rows)
	}, [props.rows])

	React.useEffect(() => {
		const fetchAndSetUser = async () => {
			await apiRef.current.setEditCellValue(gridEditCellValueParams)
		}

		void fetchAndSetUser()

		return () => {}
	}, [gridEditCellValueParams])

	const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
		<Tooltip {...props} classes={{ popper: className }} />
	))(({ theme }) => ({
		[`& .${tooltipClasses.tooltip}`]: {
			backgroundColor: theme.palette.error.main,
			color: theme.palette.error.contrastText,
		},
	}))

	const EditInputCell = (props: GridRenderEditCellParams) => {
		const { error, errorMessage } = props

		const isError = error

		return (
			<StyledTooltip open={!!isError} title={errorMessage}>
				<GridEditInputCell
					{...props}
					css={isError ? { border: '1px solid', borderColor: pink['A400'] } : {}}
				/>
			</StyledTooltip>
		)
	}

	const renderEdit = (params: GridRenderEditCellParams) => {
		return <EditInputCell {...params} />
	}

	const rederCheckboxEdit = (params: GridRenderEditCellParams) => {
		return <GridEditBooleanCell {...params} />
	}

	const newColumns: GridColDef<GridValidRowModel>[] = React.useMemo(() => {
		const indexingCol: GridColDef = {
			field: 'idx',
			headerName: 'index',
			width: 80,
			headerAlign: 'center',
			align: 'center',
			filterable: false,
			renderCell: (res: GridRenderCellParams) =>
				res.api.getAllRowIds().indexOf(res.id) + 1,
		}

		const actionCol: GridColDef = {
			field: 'actions',
			type: 'actions',
			headerName: '액션',
			width: 160,
			headerAlign: 'center',
			align: 'center',
			cellClassName: 'actions',
			getActions: (props: GridRowParams) => {
				const { row } = props
				const isInEditMode = rowModesModel[row.id]?.mode === GridRowModes.Edit

				if (isInEditMode) {
					return [
						<ActionButton
							style={{ margin: '0 0' }}
							key="rowActionSave"
							iconName="content-save"
							onClick={onSaveClick(props)}
							tooltip="저장"
							title="저장"
							iconTextColor={grey['900']}
						/>,
						<ActionButton
							style={{ margin: '0 0' }}
							key="rowActionClose"
							iconName="close"
							onClick={onCancelClick(props)}
							tooltip="취소"
							title="취소"
						/>,
					]
				}

				return [
					<ActionButton
						style={{ margin: '0 0' }}
						key="rowActionEdit"
						iconName="pencil"
						onClick={onUpdateClick(props)}
						tooltip="수정"
						title="수정"
						iconTextColor={grey['900']}
					/>,
					<ActionButton
						style={{ margin: '0 0' }}
						key="rowActionDelete"
						iconName="delete-forever"
						onClick={onDeleteClick(props)}
						tooltip="삭제"
						title="삭제"
						iconTextColor={pink['A400']}
					/>,
				]
			},
		}

		const tempColumns = props.columns.columns.map(aColumn => {
			const field = aColumn.field
			const newColumn =
				aColumn.preProcessEditCellProps !== undefined
					? { ...aColumn, renderEditCell: renderEdit }
					: aColumn.type === 'boolean'
					? { ...aColumn, renderEditCell: rederCheckboxEdit }
					: { ...aColumn }

			if (props.renderCell && props.renderCell[field] !== undefined) {
				newColumn['renderCell'] = props.renderCell[field]
			}

			if (props.renderEditCell && props.renderEditCell[field] !== undefined) {
				newColumn['renderEditCell'] = props.renderEditCell[field]
			}

			return newColumn
		})

		const res: GridColDef[] = isEditable
			? [actionCol, indexingCol, ...tempColumns]
			: [indexingCol, ...tempColumns]

		return res
	}, [props.columns, gridEditCellValueParams, rowModesModel, rows, props.rows])

	const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
		if (params.reason === GridRowEditStopReasons.rowFocusOut) {
			event.defaultMuiPrevented = true
		}
	}

	const onUpdateClick = (props: GridRowParams) => () => {
		const aUpdatedRow: GridRowModel = props.row

		setRowModesModel({ ...rowModesModel, [aUpdatedRow.id]: { mode: GridRowModes.Edit } })

		const newUpdatedRow: GridRowModel =
			aUpdatedRow.isNew === undefined ? { ...aUpdatedRow, isEdited: true } : aUpdatedRow
		setRows(rows.map(aRow => (aRow.id === aUpdatedRow.id ? newUpdatedRow : aRow)))
	}

	const onSaveClick = (props: GridRowParams) => () => {
		const { id, row } = props
		const editRows = apiRef.current.state.editRows[id]

		if (editRows !== undefined) {
			const validationResult: {
				error: boolean
				errorMessage: string
				id: GridRowId
				field: string
				value: any
			} = (() => {
				for (const aField of Object.keys(row)) {
					const thisColumn: GridColDef[] = newColumns.filter(
						aColumn => aColumn.field === aField,
					)

					if (
						thisColumn.length > 0 &&
						typeof thisColumn[0].preProcessEditCellProps === 'function'
					) {
						const newParams: GridPreProcessEditCellProps = {
							id: row.id,
							row: row,
							props: {
								value: editRows[aField].value,
							},
						}

						const res = thisColumn[0].preProcessEditCellProps(newParams) as {
							[key: string]: {
								error?: boolean
								errorMessage?: string
								value?: any
							}
						}

						if (res !== undefined && res.error !== undefined && res.error) {
							return {
								error: true,
								errorMessage:
									res.errorMessage === undefined ? '' : (res.errorMessage as string),
								id: row.id as GridRowId,
								field: aField,
								value: editRows[aField].value,
							}
						}
					}
				}

				return {
					error: false,
					errorMessage: '',
					id: row.id,
					field: '',
					value: '',
				}
			})()

			if (validationResult.error) {
				const newParams: GridEditCellValueParams & { errorMessage: string } = {
					id: validationResult.id,
					errorMessage: validationResult.errorMessage ?? '',
					field: validationResult.field,
					value: validationResult.value,
				}

				setGridEditCellValueParams(newParams)
				void apiRef.current.setEditCellValue(gridEditCellValueParams)
			} else {
				setGridEditCellValueParams({
					id: '',
					errorMessage: '',
					field: '',
					value: '',
				})
			}

			if (!validationResult.error) {
				setRowModesModel({ ...rowModesModel, [row.id]: { mode: GridRowModes.View } })
			}
		}
	}

	const onDeleteClick = (props: GridRowParams) => () => {
		const { row } = props

		setDeleteRow(row)
		setShowModal(true)
	}

	const onModalDelete = React.useCallback(() => {
		const newRows = rows.filter(aRow => {
			return aRow.id !== deleteRow.id
		})
		setRows(newRows)

		if (typeof props.onAction === 'function') {
			props.onAction('D', deleteRow)
		}
	}, [deleteRow])

	const onCancelClick = (aRow: GridRowModel) => () => {
		setRowModesModel({
			...rowModesModel,
			[aRow.id]: { mode: GridRowModes.View, ignoreModifications: true },
		})

		const editedRow = rows.find(anEditRow => anEditRow.id === aRow.id)
		if (editedRow !== undefined && editedRow.isNew) {
			setRows(rows.filter(anEditRow => anEditRow.id !== aRow.id))
		}
	}

	const processRowUpdate = (targetRow: GridRowModel) => {
		const isNew = targetRow.isNew !== undefined
		const isEdited = targetRow.isEdited !== undefined

		if (isNew || isEdited) {
			if (isNew && typeof props.onAction === 'function') {
				props.onAction('I', targetRow)
			} else if (isEdited && typeof props.onAction === 'function') {
				props.onAction('U', targetRow)
			}

			const newRows = rows.map(aRow => {
				if (aRow.id === targetRow.id) {
					const newRow = { ...aRow }
					delete newRow.isNew
					delete newRow.isEdited

					return newRow
				}

				return aRow
			})

			setRows(newRows)
		}

		return targetRow
	}

	const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
		const idArr = Object.keys(newRowModesModel)
		const id = idArr.length > 0 ? idArr[0] : ''

		if (id !== '') {
			const idx = rows.findIndex(aRow => aRow.id === id)
			const aUpdatedRow: GridRowModel = rows[idx]

			setRowModesModel({
				...rowModesModel,
				[aUpdatedRow.id]: { mode: GridRowModes.Edit },
			})

			const newUpdatedRow: GridRowModel =
				aUpdatedRow.isNew === undefined ? { ...aUpdatedRow, isEdited: true } : aUpdatedRow
			setRows(rows.map(aRow => (aRow.id === aUpdatedRow.id ? newUpdatedRow : aRow)))
		}
	}

	const onAddClick = () => {
		const id = randomId()
		setRows(oldRows => [...oldRows, { id: id, ...props.columns?.initRow, isNew: true }])
		setRowModesModel(oldModel => ({
			...oldModel,
			[id]: {
				mode: GridRowModes.Edit,
				fieldToFocus: props.columns?.fieldToFocus,
			},
		}))
	}

	return (
		<View css={style}>
			{isLoading && (
				<View className="gridLoadingCircleView">
					<CircularProgress />
				</View>
			)}
			<DataGrid
				{...props}
				rowHeight={35}
				columnHeaderHeight={35}
				apiRef={apiRef}
				rows={rows}
				columns={newColumns}
				editMode="row"
				initialState={{
					pagination: {
						paginationModel: {
							pageSize: 25,
						},
					},
				}}
				pageSizeOptions={[25, 50, 100]}
				slots={{
					toolbar: GridToolBar,
					pagination: CustomPagination,
				}}
				slotProps={{
					toolbar: { setRows, setRowModesModel, isLoading, apiRef, onAddClick },
					loadingOverlay: {
						variant: 'skeleton',
						noRowsVariant: 'skeleton',
					},
				}}
				onCellEditStop={(params: GridCellEditStopParams, event: MuiEvent) => {
					if (params.reason === GridCellEditStopReasons.cellFocusOut) {
						event.defaultMuiPrevented = true
					}
				}}
				onRowEditStop={handleRowEditStop}
				rowModesModel={rowModesModel}
				onRowSelectionModelChange={newRowSelectionModel => {
					if (typeof props.onRowSelectionModelChange === 'function') {
						props.onRowSelectionModelChange(newRowSelectionModel)
					}
				}}
				disableRowSelectionOnClick={checkboxSelection}
				checkboxSelection={checkboxSelection}
				rowSelectionModel={props.rowSelectionModel}
				indeterminateCheckboxAction="select"
				isRowSelectable={(params: GridRowParams) => params.row.status}
				// onRowModesModelChange={handleRowModesModelChange}
				onProcessRowUpdateError={error => {}}
				processRowUpdate={processRowUpdate}
				loading={isLoading}
				disableColumnSorting={isLoading}
				disableColumnMenu={isLoading}
				sx={{
					'& .MuiDataGrid-footerContainer': {
						height: '40px',
						minHeight: '40px',

						'.MuiTablePagination-root': {
							height: '100%',
							minHeight: '40px',

							'.MuiTablePagination-toolbar': {
								height: '40px',
								minHeight: '40px',
							},
						},
					},
				}}
			/>

			<Modal
				target="newGroup"
				title="삭제"
				show={showModal}
				onSubmit={onModalDelete}
				onCloseModal={() => setShowModal(false)}
				closeStatus="inherit"
				closeTitle="닫기"
				submitTitle="삭제"
				submitStatus="error"
				submitIcon="trash-can-outline"
				submitIconColor={pink['A400']}
			>
				<View css={{ width: '30rem' }}>
					<Text category="p2">이 데이터를 삭제하면 복구할 수 없습니다.</Text>
					<Text category="p2">그래도 삭제하시겠습니까?</Text>
				</View>
			</Modal>
		</View>
	)
}

const style = css({
	width: '100%',
	height: '100%',

	'.gridLoadingCircleView': {
		position: 'absolute',
		width: '40px',
		height: '40px',
		zIndex: 10,
		top: 0,
		bottom: 0,
		left: 0,
		right: 0,
		margin: 'auto',
	},
})
